/* eslint-disable react/prop-types */
// import and use only default module in order to allow spyOn(React, 'useState') in testing
import React from 'react';
import './component-video.less';
import YouTubeVideo from './subcomponents/youtube-video';
import VimeoVideo from './subcomponents/vimeo-video';
import LoomVideo from './subcomponents/loom-video';
import type { GqlComponentVideo } from '../../../content/types/content/component-video';
import { sentryCaptureException } from '../../utils/sentry/sentry-client';
import { getVideoInfo } from './utils/get-video-info';

type ComponentVideoProps = Pick<
    GqlComponentVideo,
    'videoUrl' | 'title' | 'description'
> & {
    showTitle?: boolean;
    showDescription?: boolean;
};

const ComponentVideo: React.FC<ComponentVideoProps> = ({
    videoUrl,
    title,
    description,
    showTitle = false,
    showDescription = false
}) => {
    const [hasError, setHasError] = React.useState(false);

    const handleError = React.useCallback((errorMessage: string) => {
        const error = new Error(errorMessage);
        sentryCaptureException({
            exception: error
        });
        setHasError(true);
    }, []);

    const videoElement = React.useMemo(() => {
        // Extract video info from URL
        const videoInfo = getVideoInfo(videoUrl);

        if (!videoInfo) {
            handleError(
                `Unsupported video service or invalid URL: ${videoUrl}`
            );
            return null;
        }

        // Route to appropriate video component based on service
        switch (videoInfo.service) {
            case 'YouTube':
                return (
                    <YouTubeVideo
                        videoId={videoInfo.videoId}
                        title={title}
                        onError={handleError}
                    />
                );
            case 'Vimeo':
                return (
                    <VimeoVideo
                        videoId={videoInfo.videoId}
                        title={title}
                        onError={handleError}
                    />
                );
            case 'Loom':
                return (
                    <LoomVideo
                        videoId={videoInfo.videoId}
                        title={title}
                        onError={handleError}
                    />
                );
            default: {
                // This will raise a TS error if a new type is added without including a case to handle it.
                const unknownService: never = videoInfo.service;
                handleError(`Unsupported video service: ${unknownService}`);
                return null;
            }
        }
    }, [videoUrl, title, handleError]);

    return (
        <div
            data-testid="component-video"
            className="component-video"
            role="region"
            aria-label={title}
        >
            <h3
                className={`component-video__title ${
                    !showTitle ? 'sr-only' : ''
                }`}
                id="video-title"
            >
                {title}
            </h3>
            {hasError ? (
                <div
                    className="component-video--error"
                    role="alert"
                    aria-live="polite"
                >
                    <p>
                        This video cannot be displayed. Please contact support
                        if you believe this is an error.
                    </p>
                </div>
            ) : (
                <div
                    aria-labelledby="video-title"
                    aria-describedby="video-description"
                >
                    {videoElement}
                </div>
            )}
            <p
                className={`component-video__description ${
                    !showDescription ? 'sr-only' : ''
                }`}
                id="video-description"
            >
                {description}
            </p>
        </div>
    );
};

export default ComponentVideo;
